import {
  initVersionedSchema,
  MixedSchema,
  RawInlineNodes,
} from '@principle-theorem/editor';
import {
  AutomatedNotificationType,
  AutomationCreator,
  AutomationParentScope,
  AutomationStatus,
  AutomationType,
  EMPTY_RENDERED_TEMPLATE,
  IAutomatedNotification,
  IAutomation,
  IAutomationConfiguration,
  IAutomationResource,
  IAutomationScopeData,
  IBrand,
  ICloudTask,
  IGeneratedTask,
  IMarketingCampaignEntry,
  IPractice,
  IStaffer,
  ITeam,
  TaskPriority,
  TemplateScope,
  TimingDirection,
  TimingUnit,
} from '@principle-theorem/principle-core/interfaces';
import {
  BaseMock,
  INamedTypeDocument,
  MockGenerator,
  MockTimestamp,
  Properties,
  PropertiesOf,
} from '@principle-theorem/shared';
import { MockDocRef, MockNamedDocument } from '@principle-theorem/testing';
import { ConditionLogic } from '../../condition-logic/condition-logic';

export class AutomatedNotificationMock
  extends BaseMock<Properties<IAutomatedNotification>>
  implements Properties<IAutomatedNotification>
{
  name = '';
  subject = '';
  type = AutomatedNotificationType.EMAIL;
  timing = {
    direction: TimingDirection.Before,
    amount: 1,
    unit: TimingUnit.Months,
  };
  scopeData = {
    scope: TemplateScope.Appointment,
    appointmentRef: MockDocRef(),
  };
  customTemplate = {
    content: initVersionedSchema(),
    scope: TemplateScope.Appointment,
    renderedTemplate: EMPTY_RENDERED_TEMPLATE,
  };
  requiredConditions = ConditionLogic.collection([
    ConditionLogic.and([ConditionLogic.always()]),
  ]);
}

export class GeneratedTaskMock
  extends BaseMock<PropertiesOf<IGeneratedTask>>
  implements PropertiesOf<IGeneratedTask>
{
  title = [] as RawInlineNodes;
  comment: MixedSchema = initVersionedSchema();
  assignee = MockNamedDocument<IStaffer | ITeam>();
  priority = TaskPriority.Low;
  isForTeam = false;
  timing = {
    direction: TimingDirection.Before,
    amount: 1,
    unit: TimingUnit.Months,
  };
  scope = TemplateScope.Appointment;
}

export class AutomationMock
  extends BaseMock<Properties<IAutomation<IAutomationResource>>>
  implements Properties<IAutomation<IAutomationResource>>
{
  status = AutomationStatus.Pending;
  type = AutomationType.AutomatedNotification;
  parentScope = AutomationParentScope.TreatmentStep;
  data = {
    timing: {
      direction: TimingDirection.Before,
      amount: 1,
      unit: TimingUnit.Months,
    },
    scope: TemplateScope.Appointment,
  };
  scope = TemplateScope.Appointment;
  scopeData: IAutomationScopeData = {
    scope: TemplateScope.Appointment,
    appointmentRef: MockDocRef(),
  };
  cloudTask = {
    name: 'task-name',
    createTime: MockTimestamp(),
    scheduleTime: MockTimestamp(),
    dispatchCount: 0,
    responseCount: 0,
  };
  cloudTaskHistory = [] as ICloudTask[];
  creator = {
    ...MockNamedDocument<IStaffer>(),
    type: AutomationCreator.Staffer,
  } as INamedTypeDocument<object, AutomationCreator>;
  triggerDate = MockTimestamp();
  triggerAfterDate = MockTimestamp();
  brandRef = MockDocRef<IBrand>();
  practiceRef = MockDocRef<IPractice>();
  configRef = MockDocRef<IAutomationConfiguration>();
  campaignEntryRef = MockDocRef<IMarketingCampaignEntry>();
  errorMessage = '';
}

export function MockAutomatedNotification(
  automationOverrides?: Partial<IAutomation<IAutomationResource>>,
  notificationOverrides?: Partial<IAutomatedNotification>
): IAutomation<IAutomatedNotification> {
  const data: IAutomatedNotification = {
    ...MockGenerator.generate(AutomatedNotificationMock),
    ...notificationOverrides,
  };
  return {
    ...MockGenerator.generate(AutomationMock),
    data,
    ...automationOverrides,
  } as IAutomation<IAutomatedNotification>;
}

export function MockGeneratedTask(
  automationOverrides?: Partial<IAutomation<IAutomationResource>>,
  taskOverrides: Partial<IGeneratedTask> = {}
): IAutomation<IGeneratedTask> {
  return MockGenerator.generate(AutomationMock, {
    ...automationOverrides,
    data: MockGenerator.generate(GeneratedTaskMock, { ...taskOverrides }),
  });
}
